// Finds report comments' selected text in the report. Capturing a selection
// and placing a comment walk the same text, so what is captured is found again.
//
// Only the raw text of the text nodes is used for placing. The browser's
// selection string, with its line breaks, is only for people to read.

const CONTEXT_LENGTH = 32;

// Set on the parts of the commentable root that aren't commentable text:
// charts, Fenix data blocks and the fullscreen copy of a table.
const NOT_COMMENTABLE = '[data-not-commentable]';

export const elementOf = (node) =>
    node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;

const isCommentable = (root, node) =>
    root.contains(node) && !elementOf(node).closest(NOT_COMMENTABLE);

// The commentable root's text, in document order, and where each text node
// starts in it.
const walkText = (root) => {
    const walker = root.ownerDocument.createTreeWalker(
        root,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
        {
            acceptNode: (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    return NodeFilter.FILTER_ACCEPT;
                }
                return node.matches(NOT_COMMENTABLE)
                    ? NodeFilter.FILTER_REJECT
                    : NodeFilter.FILTER_SKIP;
            },
        }
    );
    const nodes = [];
    let text = '';
    while (walker.nextNode()) {
        nodes.push({ node: walker.currentNode, start: text.length });
        text += walker.currentNode.data;
    }
    return { nodes, text };
};

const isText = (node) => node.nodeType === Node.TEXT_NODE;

const lastDescendant = (node) => {
    let last = node;
    while (last.lastChild) last = last.lastChild;
    return last;
};

// The first text node after a boundary point, within `scope`.
const textAfter = (scope, container, offset) => {
    const walker = scope.ownerDocument.createTreeWalker(
        scope,
        NodeFilter.SHOW_TEXT
    );
    if (isText(container)) {
        walker.currentNode = container;
    } else if (offset < container.childNodes.length) {
        const next = container.childNodes[offset];
        if (isText(next)) return next;
        walker.currentNode = next;
    } else {
        walker.currentNode = lastDescendant(container);
    }
    return walker.nextNode();
};

// The last text node before a boundary point, within `scope`.
const textBefore = (scope, container, offset) => {
    const walker = scope.ownerDocument.createTreeWalker(
        scope,
        NodeFilter.SHOW_TEXT
    );
    if (isText(container) || offset === 0) {
        walker.currentNode = container;
    } else {
        const previous = lastDescendant(container.childNodes[offset - 1]);
        if (isText(previous)) return previous;
        walker.currentNode = previous;
    }
    return walker.previousNode();
};

// A selection can reach past the text it covers without taking any of what
// is there. A triple-click ends at the start of the next block, say, which
// can be a chart or lie outside the report text. Narrows the range to the
// text nodes it takes characters from.
const selectedTextRange = (range) => {
    const scope = range.commonAncestorContainer;
    let { startContainer, startOffset, endContainer, endOffset } = range;
    if (!isText(startContainer) || startOffset === startContainer.length) {
        startContainer = textAfter(scope, startContainer, startOffset);
        startOffset = 0;
    }
    if (!isText(endContainer) || endOffset === 0) {
        endContainer = textBefore(scope, endContainer, endOffset);
        endOffset = endContainer?.length;
    }
    if (!startContainer || !endContainer) return null;
    const narrowed = scope.ownerDocument.createRange();
    narrowed.setStart(startContainer, startOffset);
    narrowed.setEnd(endContainer, endOffset);
    return narrowed;
};

// A point in a text node, as an offset into the walked text.
const offsetOf = ({ nodes }, container, offset) =>
    nodes.find(({ node }) => node === container).start + offset;

// Whether cutting the text at `index` would split a character that takes two
// UTF-16 code units, such as an emoji. Half of one can't be stored.
const splitsCharacter = (text, index) =>
    /[\uD800-\uDBFF]/.test(text[index - 1] ?? '') &&
    /[\uDC00-\uDFFF]/.test(text[index] ?? '');

const isBlank = (text) => !text.trim();

// The part of the reader's selection that can be commented on, or null when
// it can't be: it has to start and end in commentable text, touch nothing
// that isn't, and hold more than whitespace. Cheap enough to run on every
// change of the selection, unlike describing it.
export const commentableRange = (root, selection) => {
    if (!selection.rangeCount) return null;
    const range = selectedTextRange(selection.getRangeAt(0));
    if (
        !range ||
        !isCommentable(root, range.startContainer) ||
        !isCommentable(root, range.endContainer) ||
        Array.from(root.querySelectorAll(NOT_COMMENTABLE)).some((element) =>
            range.intersectsNode(element)
        ) ||
        isBlank(range.toString()) ||
        isBlank(selection.toString())
    ) {
        return null;
    }
    return range;
};

// Describes the reader's selection for posting, or returns null when it
// can't be commented on.
export const describeSelection = (root, selection) => {
    const range = commentableRange(root, selection);
    if (!range) return null;

    const walked = walkText(root);
    const start = offsetOf(walked, range.startContainer, range.startOffset);
    const end = offsetOf(walked, range.endContainer, range.endOffset);
    let prefixStart = Math.max(0, start - CONTEXT_LENGTH);
    if (splitsCharacter(walked.text, prefixStart)) prefixStart += 1;
    let suffixEnd = end + CONTEXT_LENGTH;
    if (splitsCharacter(walked.text, suffixEnd)) suffixEnd -= 1;

    return {
        selected_text: walked.text.slice(start, end),
        selected_text_display: selection.toString(),
        prefix: walked.text.slice(prefixStart, start),
        suffix: walked.text.slice(end, suffixEnd),
        position: start,
    };
};

// The walked text node holding the character at `index`.
const textNodeHolding = ({ nodes }, index) =>
    nodes.find(
        ({ node, start }) => start <= index && index < start + node.length
    );

// Some of the walked text, as a range. It starts in the text node holding its
// first character, and ends in the one holding its last.
const rangeOfText = (root, walked, start, end) => {
    const first = textNodeHolding(walked, start);
    const last = textNodeHolding(walked, end - 1);
    const range = root.ownerDocument.createRange();
    range.setStart(first.node, start - first.start);
    range.setEnd(last.node, end - last.start);
    return range;
};

// Every place the selected text appears, including overlapping ones.
const occurrencesOf = (text, selected) => {
    const found = [];
    for (
        let index = text.indexOf(selected);
        index !== -1;
        index = text.indexOf(selected, index + 1)
    ) {
        found.push(index);
    }
    return found;
};

// A described selection has an empty prefix only at the very start of the
// text, and an empty suffix only at its very end. Anywhere else an empty
// side would match every occurrence.
const prefixMatches = (text, start, prefix) =>
    prefix
        ? start >= prefix.length &&
          text.startsWith(prefix, start - prefix.length)
        : start === 0;

const suffixMatches = (text, end, suffix) =>
    suffix ? text.startsWith(suffix, end) : end === text.length;

// Where the comment's selected text is in the walked text, or null. The
// text itself has to be there exactly, and the text on at least one side of
// it has to be what it was. Text that matches on both sides wins, then the
// occurrence nearest to where the selection was made, then the first in the
// report. Comments saved before positions were stored have none, and for
// them the first wins outright.
const findSelectedText = (
    text,
    { selected_text, prefix, suffix, position = null }
) => {
    let best = null;
    let bestSides = 0;
    let bestDistance = Infinity;
    for (const start of occurrencesOf(text, selected_text)) {
        const sides =
            prefixMatches(text, start, prefix) +
            suffixMatches(text, start + selected_text.length, suffix);
        const distance =
            position === null ? Infinity : Math.abs(start - position);
        if (
            sides > bestSides ||
            (sides > 0 && sides === bestSides && distance < bestDistance)
        ) {
            best = start;
            bestSides = sides;
            bestDistance = distance;
        }
    }
    return best;
};

// Where each comment's selected text is in the report, in the order given,
// or null for a comment that is orphaned.
export const placeComments = (root, comments) => {
    const walked = walkText(root);
    return comments.map((comment) => {
        const { selected_text } = comment;
        const start = selected_text
            ? findSelectedText(walked.text, comment)
            : null;
        if (start === null) return null;
        return rangeOfText(root, walked, start, start + selected_text.length);
    });
};
